import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ACTIVIDAD_LABELS,
  actualizarOpcionProspecto,
  actualizarProspectoCrm,
  adaptOpcion,
  cambiarEstatusProspectoCrm,
  cambiarTemperaturaProspectoCrm,
  crearActividadProspecto,
  crearOpcionProspecto,
  eliminarOpcionProspecto,
  ESTATUS_OPCION_LABELS,
  formatCurrency,
  FINANCIAMIENTO_LABELS,
  FUENTE_TIPO_LABELS,
  getCatalogoResponsables,
  labelFromMap,
  listarActividadesProspecto,
  normalizeProspectoPayload,
  obtenerProspectoCrm,
  ORIGEN_LABELS,
  reasignarProspectoCrm,
  TEMPERATURAS_PROSPECTO,
  TEMPERATURA_LABELS,
} from '../../services/prospectosCrmService';
import { obtenerAdminInmuebles } from '../../services/adminInmueblesService';
import { listarAdminDesarrollos } from '../../services/adminDesarrollosService';
import { listarProyectos } from '../../services/proyectosInmobiliariosService';
import { listarModelos } from '../../services/proyectoModelosService';
import { listarUnidades } from '../../services/proyectoUnidadesService';
import useAuthSession from '../../hooks/useAuthSession';
import {
  ProspectoEstatusBadge,
  ProspectoTemperaturaBadge,
  ProspectoTipoBadge,
} from '../../components/crm/ProspectoBadges';
import ProspectoStatusModal from '../../components/crm/ProspectoStatusModal';
import ProspectoAsignacionModal from '../../components/crm/ProspectoAsignacionModal';
import ProspectoActividadForm from '../../components/crm/ProspectoActividadForm';
import ProspectoOpcionModal from '../../components/crm/ProspectoOpcionModal';
import ProspectoConfirmModal from '../../components/crm/ProspectoConfirmModal';
import CopilotoComercialPanel from '../../components/crm/copiloto/CopilotoComercialPanel';
import '../AdminProspectosPage.css';

const TABS = ['resumen', 'seguimiento', 'opciones', 'busqueda', 'copiloto'];

const getApiErrorMessage = (err) =>
  err.status === 403 || err.status === 404
    ? 'No tienes acceso a este prospecto.'
    : err.data?.mensaje || err.data?.message || err.message || 'No fue posible procesar la accion.';

const getWhatsappUrl = (prospecto, asesorNombre) => {
  const digits = String(prospecto?.telefonoNormalizado || prospecto?.telefono || '').replace(/\D/g, '');
  if (!digits) return '';
  const normalized = digits.length === 10 ? `52${digits}` : digits;
  const text = `Hola ${prospecto.nombreContacto || prospecto.nombre}, soy ${asesorNombre || prospecto.asesorNombre || 'tu asesor'} de CN Inmobiliaria. Te contacto para dar seguimiento a tu busqueda.`;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(text)}`;
};

const toEditForm = (prospecto = {}) => ({
  tipoProspecto: prospecto.tipoProspecto || 'INTERES_ESPECIFICO',
  nombre: prospecto.nombreContacto || prospecto.nombre || '',
  apellidoPaterno: prospecto.apellidoPaterno || '',
  apellidoMaterno: prospecto.apellidoMaterno || '',
  codigoNumeroPaisId: prospecto.codigoNumeroPaisId || '',
  telefono: prospecto.telefono || '',
  email: prospecto.email || '',
  medioContactoPreferido: prospecto.medioContactoPreferido || '',
  origen: prospecto.origen || '',
  origenDetalle: prospecto.origenDetalle || '',
  inmuebleId: prospecto.inmuebleId || '',
  desarrolloId: prospecto.desarrolloId || '',
  proyectoId: prospecto.proyectoId || '',
  modeloId: prospecto.modeloId || '',
  unidadId: prospecto.unidadId || '',
  mensajeInicial: prospecto.mensajeInicial || '',
  tipoOperacionBuscada: prospecto.tipoOperacionBuscada || '',
  tipoInmuebleBuscado: prospecto.tipoInmuebleBuscado || '',
  zonaInteres: prospecto.zonaInteres || '',
  zonasAlternativas: prospecto.zonasAlternativas || '',
  presupuestoMinimo: prospecto.presupuestoMinimo || '',
  presupuestoMaximo: prospecto.presupuestoMaximo || '',
  engancheDisponible: prospecto.engancheDisponible || '',
  tipoFinanciamiento: prospecto.tipoFinanciamiento || '',
  estatusCredito: prospecto.estatusCredito || '',
  montoCredito: prospecto.montoCredito || '',
  institucionCredito: prospecto.institucionCredito || '',
  recamarasMinimas: prospecto.recamarasMinimas || '',
  banosMinimos: prospecto.banosMinimos || '',
  estacionamientosMinimos: prospecto.estacionamientosMinimos || '',
  superficieTerrenoMinima: prospecto.superficieTerrenoMinima || '',
  superficieConstruccionMinima: prospecto.superficieConstruccionMinima || '',
  aceptaUsado: prospecto.aceptaUsado === true,
  aceptaPreventa: prospecto.aceptaPreventa === true,
  aceptaMunicipiosCercanos: prospecto.aceptaMunicipiosCercanos === true,
  fechaEstimadaCompra: prospecto.fechaEstimadaCompra || '',
  requisitosBusqueda: prospecto.requisitosBusqueda || '',
  captacionTipoOperacion: prospecto.captacionTipoOperacion || '',
  captacionTipoInmueble: prospecto.captacionTipoInmueble || '',
  captacionUbicacion: prospecto.captacionUbicacion || '',
  captacionDescripcion: prospecto.captacionDescripcion || '',
  captacionPrecioEstimado: prospecto.captacionPrecioEstimado || '',
  estatus: prospecto.estatus || 'NUEVO',
  temperatura: prospecto.temperatura || 'TIBIO',
  fechaProximoSeguimiento: prospecto.fechaProximoSeguimientoRaw ? String(prospecto.fechaProximoSeguimientoRaw).slice(0, 16) : '',
  asesorId: prospecto.asesorResponsableId || prospecto.asesorId || '',
  supervisorId: prospecto.supervisorId || '',
});

const getAtribucionTexto = (prospecto = {}) => {
  if (!prospecto.asesorAtribucionNombre && !prospecto.asesorResponsableNombre) {
    return {
      origen: 'Sin referencia de asesor',
      responsable: 'Pendiente de asignacion',
    };
  }

  if (
    prospecto.asesorAtribucionNombre &&
    prospecto.asesorResponsableNombre &&
    prospecto.asesorAtribucionNombre === prospecto.asesorResponsableNombre
  ) {
    return {
      combinado: `Asesor de origen y responsable: ${prospecto.asesorResponsableNombre}`,
    };
  }

  return {
    origen: prospecto.asesorAtribucionNombre
      ? `Asesor que origino el prospecto: ${prospecto.asesorAtribucionNombre}`
      : 'Sin referencia de asesor',
    responsable: prospecto.asesorResponsableNombre
      ? `Asesor responsable actual: ${prospecto.asesorResponsableNombre}`
      : 'Pendiente de asignacion',
  };
};

function Field({ label, value }) {
  return (
    <article className="crm-prospectos-info-item">
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </article>
  );
}

export default function AdminProspectoDetallePage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sesion = useAuthSession();
  const [prospecto, setProspecto] = useState(null);
  const [actividades, setActividades] = useState([]);
  const [opciones, setOpciones] = useState([]);
  const [responsables, setResponsables] = useState({ asesores: [], supervisores: [] });
  const [inmuebles, setInmuebles] = useState([]);
  const [desarrollos, setDesarrollos] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [tab, setTab] = useState(searchParams.get('tab') || 'resumen');
  const [editando, setEditando] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [statusModal, setStatusModal] = useState(false);
  const [asignacionModal, setAsignacionModal] = useState(searchParams.get('accion') === 'reasignar');
  const [opcionModal, setOpcionModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoResponsables, setCargandoResponsables] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [errorResponsables, setErrorResponsables] = useState('');
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  const rolGlobal = String(sesion.rolGlobal || '').toUpperCase();
  const puedeReasignar = ['ADMIN', 'SUPERADMIN', 'SUPERVISOR'].includes(rolGlobal) || sesion.esAdminCn;
  const puedeUsarCopiloto = ['ASESOR', 'SUPERVISOR', 'ADMIN', 'SUPERADMIN'].includes(rolGlobal) || sesion.esAdminCn;
  const whatsappUrl = useMemo(() => prospecto ? getWhatsappUrl(prospecto, sesion.nombre) : '', [prospecto, sesion.nombre]);
  const mostrarDatosBusqueda = ['BUSQUEDA_ACTIVA', 'INVERSIONISTA', 'CAPTACION_PROPIETARIO'].includes(prospecto?.tipoProspecto);
  const atribucionTexto = getAtribucionTexto(prospecto || {});

  const cargarDetalle = useCallback(async (options = {}) => {
    setCargando(true);
    setError('');
    setProspecto(null);
    setActividades([]);
    setOpciones([]);

    try {
      const [detalle, actividadesData] = await Promise.all([
        obtenerProspectoCrm(id, { signal: options.signal, suppressForbiddenAlert: true }),
        listarActividadesProspecto(id, { signal: options.signal, suppressForbiddenAlert: true }).catch(() => []),
      ]);
      setProspecto(detalle);
      setEditForm(toEditForm(detalle));
      setActividades(actividadesData);
      setOpciones((detalle.raw?.opciones || detalle.raw?.opcionesInmobiliarias || []).map(adaptOpcion));
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(getApiErrorMessage(err));
      }
    } finally {
      if (!options.signal?.aborted) {
        setCargando(false);
      }
    }
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    cargarDetalle({ signal: controller.signal });
    return () => controller.abort();
  }, [cargarDetalle]);

  const cargarResponsables = useCallback(async (options = {}) => {
    setCargandoResponsables(true);
    setErrorResponsables('');

    try {
      setResponsables(await getCatalogoResponsables(options));
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('No fue posible cargar responsables CRM', err);
        setErrorResponsables('No fue posible cargar responsables comerciales.');
      }
    } finally {
      if (!options.signal?.aborted) {
        setCargandoResponsables(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const cargarCatalogos = async () => {
      const [inmueblesData, desarrollosData, proyectosData] = await Promise.all([
        obtenerAdminInmuebles({ signal: controller.signal }).catch(() => []),
        listarAdminDesarrollos({ signal: controller.signal }).catch(() => []),
        listarProyectos({ soloActivos: true, signal: controller.signal }).catch(() => []),
      ]);
      setInmuebles(inmueblesData);
      setDesarrollos(desarrollosData);
      setProyectos(proyectosData);
    };

    cargarCatalogos();
    cargarResponsables({ signal: controller.signal });
    return () => controller.abort();
  }, [cargarResponsables]);

  const cargarDatosProyecto = useCallback(async (proyectoId) => {
    if (!proyectoId) {
      setModelos([]);
      setUnidades([]);
      return;
    }

    const [modelosData, unidadesData] = await Promise.all([
      listarModelos(proyectoId, { soloActivos: true }).catch(() => []),
      listarUnidades(proyectoId, { soloActivas: true }).catch(() => []),
    ]);
    setModelos(modelosData);
    setUnidades(unidadesData);
  }, []);

  useEffect(() => {
    if (editForm?.proyectoId) {
      cargarDatosProyecto(editForm.proyectoId);
    }
  }, [cargarDatosProyecto, editForm?.proyectoId]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    setSearchParams(nextTab === 'resumen' ? {} : { tab: nextTab });
  };

  const guardarActividad = async (payload) => {
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await crearActividadProspecto(id, payload);
      setMensaje('Actividad registrada correctamente.');
      await cargarDetalle();
      return true;
    } catch (err) {
      setError(getApiErrorMessage(err));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const guardarEstatus = async (payload) => {
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await cambiarEstatusProspectoCrm(id, payload);
      setMensaje('Estatus actualizado correctamente.');
      setStatusModal(false);
      await cargarDetalle();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  const cambiarTemperatura = async (temperatura) => {
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await cambiarTemperaturaProspectoCrm(id, { temperatura });
      setMensaje('Temperatura actualizada correctamente.');
      await cargarDetalle();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  const guardarAsignacion = async (payload) => {
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await reasignarProspectoCrm(id, payload);
      setMensaje('Asignacion actualizada correctamente.');
      setAsignacionModal(false);
      await cargarDetalle();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  const guardarOpcion = async (payload) => {
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      if (opcionModal?.id) {
        await actualizarOpcionProspecto(id, opcionModal.id, payload);
        setMensaje('Opcion actualizada correctamente.');
      } else {
        await crearOpcionProspecto(id, payload);
        setMensaje('Opcion agregada correctamente.');
      }
      setOpcionModal(null);
      await cargarDetalle();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  const quitarOpcion = async () => {
    if (!confirmDelete?.id) return;
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await eliminarOpcionProspecto(id, confirmDelete.id);
      setMensaje('Opcion eliminada correctamente.');
      setConfirmDelete(null);
      await cargarDetalle();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  const updateEditForm = (event) => {
    const { checked, name, type, value } = event.target;
    setEditForm((actual) => ({
      ...actual,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'proyectoId' ? { modeloId: '', unidadId: '' } : {}),
    }));
  };

  const guardarEdicion = async (event) => {
    event.preventDefault();
    setGuardando(true);
    setError('');
    setMensaje('');

    try {
      await actualizarProspectoCrm(id, normalizeProspectoPayload(editForm));
      setMensaje('Informacion actualizada correctamente.');
      setEditando(false);
      await cargarDetalle();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <main className="crm-prospectos">
        <p className="crm-prospectos-feedback">Cargando ficha del prospecto...</p>
      </main>
    );
  }

  if (error && !prospecto) {
    return (
      <main className="crm-prospectos">
        <p className="crm-prospectos-feedback is-error">{error}</p>
        <button type="button" onClick={() => cargarDetalle()}>Reintentar</button>
      </main>
    );
  }

  return (
    <main className="crm-prospectos">
      <section className="crm-prospectos-detail-hero">
        <div>
          <Link to="/admin/prospectos">Volver a prospectos</Link>
          <h1>{prospecto.nombre}</h1>
          <div className="crm-prospectos-hero-badges">
            <ProspectoTipoBadge tipo={prospecto.tipoProspecto} />
            <ProspectoEstatusBadge estatus={prospecto.estatus} />
            <ProspectoTemperaturaBadge temperatura={prospecto.temperatura} />
          </div>
          <p>
            Responsable actual: <strong>{prospecto.asesorResponsableNombre || 'Sin asignar'}</strong>
            {prospecto.supervisorNombre ? ` · Supervisor: ${prospecto.supervisorNombre}` : ''}
            {' · '}Origen: {labelFromMap(ORIGEN_LABELS, prospecto.origen)}
          </p>
          <p className={prospecto.seguimientoVencido ? 'crm-prospectos-next is-overdue' : 'crm-prospectos-next'}>
            Proximo seguimiento: {prospecto.fechaProximoSeguimiento}
          </p>
        </div>
        <div className="crm-prospectos-hero-actions">
          {whatsappUrl ? <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp</a> : <button type="button" disabled title="Sin telefono">WhatsApp</button>}
          <button type="button" onClick={() => setEditando(true)}>Editar</button>
          <button type="button" onClick={() => setStatusModal(true)}>Cambiar estatus</button>
          <button type="button" onClick={() => changeTab('seguimiento')}>Registrar actividad</button>
          {puedeReasignar ? <button type="button" onClick={() => setAsignacionModal(true)}>Reasignar</button> : null}
        </div>
      </section>

      {mensaje ? <p className="crm-prospectos-feedback is-ok">{mensaje}</p> : null}
      {error ? <p className="crm-prospectos-feedback is-error">{error}</p> : null}

      <nav className="crm-prospectos-tabs" aria-label="Ficha de prospecto">
        {TABS.filter((item) => {
          if (item === 'busqueda') return mostrarDatosBusqueda;
          if (item === 'copiloto') return puedeUsarCopiloto;
          return true;
        }).map((item) => (
          <button key={item} type="button" className={tab === item ? 'is-active' : ''} onClick={() => changeTab(item)}>
            {item === 'resumen' ? 'Resumen' : item === 'seguimiento' ? 'Seguimiento' : item === 'opciones' ? 'Opciones inmobiliarias' : item === 'copiloto' ? 'Copiloto Comercial' : 'Datos de busqueda'}
          </button>
        ))}
      </nav>

      {tab === 'resumen' ? (
        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head">
            <h2>Resumen</h2>
            <button type="button" onClick={() => setEditando(true)}>Editar informacion</button>
          </div>
          <div className="crm-prospectos-info-grid">
            <Field label="Telefono" value={prospecto.telefono} />
            <Field label="Correo" value={prospecto.email} />
            <Field label="Origen" value={labelFromMap(ORIGEN_LABELS, prospecto.origen)} />
            <Field label="Detalle origen" value={prospecto.origenDetalle} />
            <Field label="Interes" value={prospecto.interes} />
            <Field label="Inmueble" value={prospecto.inmuebleNombre} />
            <Field label="Desarrollo" value={prospecto.desarrolloNombre} />
            <Field label="Proyecto" value={prospecto.proyectoNombre} />
            <Field label="Modelo" value={prospecto.modeloNombre} />
            <Field label="Unidad" value={prospecto.unidadCodigo} />
            <Field label="Financiamiento" value={labelFromMap(FINANCIAMIENTO_LABELS, prospecto.tipoFinanciamiento, '')} />
            <Field label="Credito" value={labelFromMap({}, prospecto.estatusCredito, '')} />
            <Field label="Monto credito" value={formatCurrency(prospecto.montoCredito)} />
            <Field label="Fecha registro" value={prospecto.fechaRegistro} />
            <Field label="Ultima actividad" value={prospecto.fechaUltimaActividad} />
            <Field label="Motivo perdida" value={prospecto.motivoPerdida} />
          </div>
          <div className="crm-prospectos-note-block">
            <strong>Origen y atribucion comercial</strong>
            <div className="crm-prospectos-info-grid crm-prospectos-origin-grid">
              <Field label="Fuente" value={labelFromMap(FUENTE_TIPO_LABELS, prospecto.fuenteTipo, 'Fuente sin definir')} />
              <Field label="Canal" value={labelFromMap(ORIGEN_LABELS, prospecto.origen)} />
              <Field label="Proyecto / desarrollo / inmueble" value={prospecto.proyectoNombre || prospecto.desarrolloNombre || prospecto.inmuebleNombre} />
              <Field label="Codigo de referido" value={prospecto.codigoReferido} />
              {atribucionTexto.combinado ? (
                <Field label="Atribucion y responsable" value={atribucionTexto.combinado} />
              ) : (
                <>
                  <Field label="Origen comercial" value={atribucionTexto.origen} />
                  <Field label="Responsable actual" value={atribucionTexto.responsable} />
                </>
              )}
            </div>
          </div>
          {prospecto.mensajeInicial ? (
            <div className="crm-prospectos-note-block">
              <strong>Notas / mensaje inicial</strong>
              <p>{prospecto.mensajeInicial}</p>
            </div>
          ) : null}
        </section>
      ) : null}

      {tab === 'seguimiento' ? (
        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head">
            <h2>Seguimiento</h2>
            <div className="crm-prospectos-inline-actions">
              <button type="button" onClick={() => setStatusModal(true)}>Cambiar estatus</button>
              {TEMPERATURAS_PROSPECTO.map((temperatura) => (
                <button key={temperatura} type="button" onClick={() => cambiarTemperatura(temperatura)} disabled={guardando || prospecto.temperatura === temperatura}>
                  {labelFromMap(TEMPERATURA_LABELS, temperatura)}
                </button>
              ))}
            </div>
          </div>
          <ProspectoActividadForm onSubmit={guardarActividad} saving={guardando} />
          <div className="crm-prospectos-timeline">
            {actividades.length === 0 ? <p className="crm-prospectos-empty">Sin actividades registradas.</p> : null}
            {actividades.map((actividad) => (
              <article key={actividad.id || `${actividad.fecha}-${actividad.comentario}`}>
                <div>
                  <strong>{labelFromMap(ACTIVIDAD_LABELS, actividad.tipoActividad)}</strong>
                  <span>{actividad.fecha}</span>
                </div>
                <p>{actividad.comentario}</p>
                <small>{actividad.usuarioNombre}</small>
                {actividad.estatusNuevo ? <small>Estatus: {actividad.estatusAnterior || '-'} a {actividad.estatusNuevo}</small> : null}
                {actividad.temperaturaNueva ? <small>Temperatura: {actividad.temperaturaAnterior || '-'} a {actividad.temperaturaNueva}</small> : null}
                {actividad.fechaProximoSeguimiento ? <small>Nuevo seguimiento: {actividad.fechaProximoSeguimiento}</small> : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {tab === 'opciones' ? (
        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head">
            <h2>Opciones inmobiliarias</h2>
            <button type="button" className="is-primary" onClick={() => setOpcionModal({})}>+ Agregar opcion</button>
          </div>
          <div className="crm-prospectos-options-grid">
            {opciones.length === 0 ? <p className="crm-prospectos-empty">Sin opciones inmobiliarias registradas.</p> : null}
            {opciones.map((opcion) => (
              <article key={opcion.id} className="crm-prospectos-option-card">
                <div>
                  <strong>{opcion.nombre}</strong>
                  <span>{labelFromMap(ESTATUS_OPCION_LABELS, opcion.estatus)}</span>
                </div>
                <p>{opcion.comentarios || 'Sin comentarios'}</p>
                <small>{opcion.tipoReferencia} · Sugerida: {opcion.fechaSugerida}</small>
                {opcion.fechaActualizacion ? <small>Actualizada: {opcion.fechaActualizacion}</small> : null}
                <div className="crm-prospectos-actions">
                  {opcion.inmuebleId ? <Link to={`/admin/inmuebles/editar/${opcion.inmuebleId}`}>Ver inmueble</Link> : null}
                  {opcion.proyectoId ? <Link to={`/admin/proyectos-inmobiliarios/${opcion.proyectoId}/editar`}>Ver proyecto</Link> : null}
                  {opcion.desarrolloId ? <Link to={`/admin/desarrollos/${opcion.desarrolloId}/editar`}>Ver desarrollo</Link> : null}
                  <button type="button" onClick={() => setOpcionModal(opcion)}>Editar</button>
                  <button type="button" className="is-danger" onClick={() => setConfirmDelete(opcion)}>Quitar</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {tab === 'busqueda' && mostrarDatosBusqueda ? (
        <section className="crm-prospectos-card">
          <div className="crm-prospectos-section-head"><h2>Datos de busqueda</h2></div>
          <div className="crm-prospectos-info-grid">
            <Field label="Operacion" value={labelFromMap({}, prospecto.tipoOperacionBuscada, '') || labelFromMap({}, prospecto.captacionTipoOperacion, '')} />
            <Field label="Tipo de inmueble" value={labelFromMap({}, prospecto.tipoInmuebleBuscado, '') || labelFromMap({}, prospecto.captacionTipoInmueble, '')} />
            <Field label="Zona" value={prospecto.zonaInteres || prospecto.captacionUbicacion} />
            <Field label="Zonas alternativas" value={prospecto.zonasAlternativas} />
            <Field label="Presupuesto minimo" value={formatCurrency(prospecto.presupuestoMinimo)} />
            <Field label="Presupuesto maximo" value={formatCurrency(prospecto.presupuestoMaximo)} />
            <Field label="Enganche" value={formatCurrency(prospecto.engancheDisponible)} />
            <Field label="Financiamiento" value={labelFromMap(FINANCIAMIENTO_LABELS, prospecto.tipoFinanciamiento, '')} />
            <Field label="Credito" value={labelFromMap({}, prospecto.estatusCredito, '')} />
            <Field label="Monto credito" value={formatCurrency(prospecto.montoCredito)} />
            <Field label="Recamaras minimas" value={prospecto.recamarasMinimas} />
            <Field label="Banos minimos" value={prospecto.banosMinimos} />
            <Field label="Estacionamientos minimos" value={prospecto.estacionamientosMinimos} />
            <Field label="Terreno minimo" value={prospecto.superficieTerrenoMinima} />
            <Field label="Construccion minima" value={prospecto.superficieConstruccionMinima} />
            <Field label="Fecha estimada" value={prospecto.fechaEstimadaCompra} />
            <Field label="Acepta usado" value={prospecto.aceptaUsado ? 'Si' : 'No'} />
            <Field label="Acepta preventa" value={prospecto.aceptaPreventa ? 'Si' : 'No'} />
            <Field label="Acepta municipios cercanos" value={prospecto.aceptaMunicipiosCercanos ? 'Si' : 'No'} />
          </div>
          <div className="crm-prospectos-note-block">
            <strong>Requisitos / descripcion</strong>
            <p>{prospecto.requisitosBusqueda || prospecto.captacionDescripcion || '-'}</p>
          </div>
        </section>
      ) : null}

      {tab === 'copiloto' && puedeUsarCopiloto ? (
        <CopilotoComercialPanel
          prospecto={prospecto}
          proyectos={proyectos}
          desarrollos={desarrollos}
          puedeUsar={puedeUsarCopiloto}
        />
      ) : null}

      {editando ? (
        <div className="crm-prospectos-modal-overlay" role="presentation" onMouseDown={() => setEditando(false)}>
          <section className="crm-prospectos-modal is-wide" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="crm-prospectos-modal-head">
              <div>
                <p className="crm-prospectos-eyebrow">Prospecto</p>
                <h2>Editar informacion</h2>
              </div>
              <button type="button" onClick={() => setEditando(false)} disabled={guardando} aria-label="Cerrar">x</button>
            </div>
            <form className="crm-prospectos-modal-body" onSubmit={guardarEdicion}>
              <div className="crm-prospectos-form-grid">
                <label><span>Nombre</span><input name="nombre" value={editForm.nombre} onChange={updateEditForm} required /></label>
                <label><span>Apellido paterno</span><input name="apellidoPaterno" value={editForm.apellidoPaterno} onChange={updateEditForm} /></label>
                <label><span>Apellido materno</span><input name="apellidoMaterno" value={editForm.apellidoMaterno} onChange={updateEditForm} /></label>
                <label><span>Telefono</span><input name="telefono" value={editForm.telefono} onChange={updateEditForm} /></label>
                <label><span>Correo</span><input name="email" type="email" value={editForm.email} onChange={updateEditForm} /></label>
                <label><span>Detalle origen</span><input name="origenDetalle" value={editForm.origenDetalle} onChange={updateEditForm} /></label>
                <label className="is-full"><span>Mensaje / notas</span><textarea name="mensajeInicial" value={editForm.mensajeInicial} onChange={updateEditForm} rows="4" /></label>
                <label><span>Zona interes</span><input name="zonaInteres" value={editForm.zonaInteres} onChange={updateEditForm} /></label>
                <label><span>Presupuesto maximo</span><input name="presupuestoMaximo" type="number" min="0" value={editForm.presupuestoMaximo} onChange={updateEditForm} /></label>
                <label><span>Proximo seguimiento</span><input name="fechaProximoSeguimiento" type="datetime-local" value={editForm.fechaProximoSeguimiento} onChange={updateEditForm} /></label>
              </div>
              <div className="crm-prospectos-modal-actions">
                <button type="submit" className="is-primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar cambios'}</button>
                <button type="button" onClick={() => setEditando(false)} disabled={guardando}>Cancelar</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {statusModal ? <ProspectoStatusModal prospecto={prospecto} onClose={() => setStatusModal(false)} onSubmit={guardarEstatus} saving={guardando} /> : null}
      {asignacionModal ? (
        <ProspectoAsignacionModal
          prospecto={prospecto}
          asesores={responsables.asesores}
          supervisores={responsables.supervisores}
          cargandoResponsables={cargandoResponsables}
          errorResponsables={errorResponsables}
          onRetryResponsables={() => cargarResponsables()}
          onClose={() => setAsignacionModal(false)}
          onSubmit={guardarAsignacion}
          saving={guardando}
        />
      ) : null}
      {opcionModal ? (
        <ProspectoOpcionModal
          opcion={opcionModal.id ? opcionModal : null}
          inmuebles={inmuebles}
          desarrollos={desarrollos}
          proyectos={proyectos}
          modelos={modelos}
          unidades={unidades}
          onProyectoChange={cargarDatosProyecto}
          onClose={() => setOpcionModal(null)}
          onSubmit={guardarOpcion}
          saving={guardando}
        />
      ) : null}
      {confirmDelete ? (
        <ProspectoConfirmModal
          title="Quitar opcion"
          message={`Deseas quitar "${confirmDelete.nombre}" de este prospecto?`}
          confirmLabel="Quitar opcion"
          onConfirm={quitarOpcion}
          onCancel={() => setConfirmDelete(null)}
          busy={guardando}
        />
      ) : null}
    </main>
  );
}
